import moment from 'moment';
import {getMagpieChartColors, getMagpieDonutChartColors} from './magpie-colors';

const ANIMATION_DURATION = 1200;

function getChartSelector(element) {
  return '#' + element.find('.chart').attr('id') + ' > svg';
}

function registerResizeListener($scope, $window, update) {
  angular.element($window).on('resize', update);
  $scope.$on('$destroy', function() {
    angular.element($window).off('resize', update);
  });
}

function calcMinMax(data) {
  return _.reduce(data, function(result, v) {
    return {
      min: Math.min(result.min, v),
      max: Math.max(result.max, v)
    };
  }, {
    min: Number.POSITIVE_INFINITY,
    max: Number.NEGATIVE_INFINITY
  });
}

const historicalBarChartMargin = {
  top: 10,
  right: 10,
  bottom: 50,
  left: 70
};

angular.module('magpieUtil', []).
filter('prettyJSON', function() {
  return function prettyPrintJson(json) {
    if (_.isString(json)) {
      try {
        json = JSON.parse(json);
      } catch (e) {
        // ignore it, must not be a json string
      }
    }
    return JSON.stringify(angular.copy(json), null, '  ');
  };
}).
filter('nonEmpty', ['$filter', function($filter) {
  return function nonEmpty(obj) {
    return _.size(obj) > 0;
  };
}]).
filter('unspaceCurlyBraces', ['$filter', function($filter) {
  return function unspaceCurlyBraces(str) {
    return str.replace('}    }', '}}');
  };
}]).
filter('score', ['$filter', function($filter) {
  return function printScore(v) {
    return $filter('number')(v * 100, 0);
  };
}]).
filter('percent', ['$filter', function($filter) {
  return function printPercent(v) {
    return $filter('score')(v) + '%';
  };
}]).
filter('timestamp', ['$filter', function($filter) {
  return function printTimestamp(timestamp) {
    return $filter('date')(timestamp, 'yyyy-MM-dd HH:mm:ss Z');
  };
}]).
filter('utcTimestamp', function() {
  return function printUtcTimestamp(timestamp) {
    if (timestamp) {
      return moment.utc(timestamp).format('YYYY-MM-DD HH:mm:ss');
    } else {
      return timestamp;
    }
  };
}).
filter('elapsedTime', function() {
  return function(action) {
    if (action) {
      const start = moment(action.startTime || action.timestamp);
      const end = moment(action.endTime || action.timestamp);
      return end.diff(start) / 1000.0;
    }
  };
}).filter('prettyDuration', function() {
  return function(time, includeSeconds = true) {
    if (time !== undefined) {
      let twoDigits = new Intl.NumberFormat('en-US', {minimumIntegerDigits: 2});
      let threeDigits = new Intl.NumberFormat('en-US', {minimumIntegerDigits: 3});

      let duration = moment.duration(time, 'seconds');

      let dayPart = Math.floor(duration.asDays()) ? Math.floor(duration.asDays()) + 'd ' : '';
      let hourPart = twoDigits.format(duration.hours()) + 'h ';
      let minPart = twoDigits.format(duration.minutes()) + 'm ';
      let secPart = includeSeconds ? twoDigits.format(duration.seconds()) + '.' +
        threeDigits.format(duration.milliseconds()) + 's' : '';

      return dayPart + hourPart + minPart + secPart;
    }
  };
}).filter('msToDuration', function() {
  return function(millis) {
    let milliseconds = millis % 1000;
    let seconds = Math.floor(millis / 1000);
    let minute = Math.floor(seconds / 60);
    seconds = seconds % 60;
    let hour = Math.floor(minute / 60);
    minute = minute % 60;
    let day = Math.floor(hour / 24);
    hour = hour % 24;

    let timeoutText = '';
    if (day > 0) {
      timeoutText = timeoutText + day + ' days ';
    } if (hour > 0) {
      timeoutText = timeoutText + hour + ' hours ';
    } if (minute > 0) {
      timeoutText = timeoutText + minute + ' minutes ';
    } if (seconds > 0) {
      timeoutText = timeoutText + seconds + ' seconds ';
    } if (milliseconds > 0) {
      timeoutText = timeoutText + milliseconds + ' milliseconds ';
    }
    return timeoutText;
  };
}).
filter('bytesToGibibytes', function() {
  return function(bytes) {
    return bytes / (1024 * 1024 * 1024);
  };
}).
filter('withUnit', function() {
  return function(value, unit) {
    return value + ' ' + unit;
  };
}).
filter('prettyInlineList', function() {
  return function(values) {
    return values.join(', ');
  };
}).
filter('itemGetter', function() {
  return function(values, key) {
    return values.map((v) => v[key]);
  };
}).
directive('magpieActionState', function() {
  return {
    restrict: 'E',
    scope: {
      state: '='
    },
    templateUrl: 'components/magpie-display/util/action-state.html',
    controller: function($scope) {
      function getType(state) {
        switch (state) {
          case 'Succeeded':
          case 'Running':
          case 'Finished':
            return 'success';
          case 'Failed':
            return 'danger';
          case 'Preparing':
          case 'InProgress':
          case 'Provisioning':
          case 'Bootstrapping':
          case 'Stopping':
            return 'info';
          default:
            return 'default';
        }
      }

      $scope.label = _.startCase($scope.state);
      $scope.type = getType($scope.state);
    }
  };
}).
directive('magpieHealthState', ['$filter', function($filter) {
  return {
    restrict: 'E',
    scope: {
      state: '='
    },
    templateUrl: 'components/magpie-display/util/health-state.html',
    controller: function($scope) {
      function getType(state) {
        switch (state) {
          case 'Healthy':
            return 'success';
          case 'Unhealthy':
            return 'danger';
          case 'Degraded':
            return 'warning';
          default:
            return 'default';
        }
      }

      $scope.label = _.startCase($scope.state.state);
      $scope.type = getType($scope.state.state);
      const lastChanged = $filter('timestamp')($scope.state.lastChanged);
      $scope.tooltip = `Since ${lastChanged}`;
    }
  };
}]).
filter('exceptionDetails', function() {
  return function renderExceptionDetails(details) {
    if (details.stackTrace) {
      return details.stackTrace;
    } else if (details.message) {
      return `${details.exceptionType}: ${details.message}`;
    } else {
      return details.exceptionType;
    }
  };
}).
directive('magpieGaugeChart', ['$timeout', '$window', function($timeout, $window) {
  return {
    restrict: 'E',
    scope: {
      value: '=',
      total: '=',
      title: '=',
      id: '=',
      type: '='
    },
    templateUrl: 'components/magpie-display/util/gauge-chart-template.html',
    compile: function() {
      return function linkFunction($scope, element) {
        const chart = nv.models
          .pieChart()
          .x((d) => d.label)
          .y((d) => d.value)
          // we want this to be a half donut
          .startAngle(function(d) {
            return d.startAngle / 2.0 - Math.PI / 2.0;
          })
          .endAngle(function(d) {
            return d.endAngle / 2.0 - Math.PI / 2.0;
          })
          .donut(true)
          .donutRatio(0.35)
          .showLegend(false)
          .showLabels(false)
          .margin({
            top: 0,
            right: 10,
            bottom: -70,
            left: 10
          })
          .title($scope.title + ' Values')
          .titleOffset(15)
          .showTooltipPercent(true)
          .color(getMagpieDonutChartColors());

        $timeout(function() {
          const selector = getChartSelector(element);

          // build the chart
          d3.select(selector)
            .datum([
              {
                label: $scope.title,
                value: $scope.value
              },
              {
                label: 'Not ' + $scope.title,
                value: $scope.total - $scope.value
              }
            ])
            .transition()
            .duration(ANIMATION_DURATION)
            .call(chart);

          const svg = angular.element(selector);

          function update() {
            chart.update();

            // set the text elements to black
            svg.find('text')
              .css({
                'font-size': '14px',
                'fill': 'black'
              });
          }

          update();
          registerResizeListener($scope, $window, update);
        });
      };
    }
  };
}]).
directive('magpiePieChart', ['$timeout', '$window', function($timeout, $window) {
  return {
    restrict: 'E',
    scope: {
      id: '=',
      type: '=',
      chartData: '=',
      title: '=',
      width: '=',
      height: '='
    },
    templateUrl: 'components/magpie-display/util/pie-chart-template.html',
    compile: function() {
      return function linkFunction($scope, element) {
        const chart = nv.models
          .pieChart()
          .x((d) => d.label)
          .y((d) => d.value)
          .donut(true)
          .donutRatio(0.4)
          .showLegend(false)
          .showLabels(false)
          .title($scope.title)
          .showTooltipPercent(true)
          .color(getMagpieChartColors());

        $timeout(function() {
          const selector = getChartSelector(element);

          // build the chart
          d3.select(selector)
            .datum($scope.chartData)
            .transition()
            .duration(ANIMATION_DURATION)
            .call(chart);

          // set the text elements to black
          angular.element(selector)
            .find('text')
            .css({
              'fill': 'black'
            });

          registerResizeListener($scope, $window, chart.update);
        });
      };
    }
  };
}]).
directive('magpieHistogram', ['$timeout', '$window', '$filter', function($timeout, $window,
                                                                         $filter) {
  return {
    restrict: 'E',
    scope: {
      id: '=',
      histogram: '='
    },
    templateUrl: 'components/magpie-display/util/histogram-template.html',
    compile: function() {
      return function linkFunction($scope, element) {
        const chart = nv.models
          .historicalBarChart()
          .margin(historicalBarChartMargin)
          .useInteractiveGuideline(true)
          .color(getMagpieChartColors());

        const xTickFormat = d3.format(',');

        chart.xAxis
          .showMaxMin(false)
          .tickFormat(xTickFormat);

        chart.yAxis
          .showMaxMin(false)
          .tickFormat(d3.format(',.0f'));

        $timeout(function() {
          const selector = getChartSelector(element);

          const counts = $scope.histogram.counts;
          const bins = $scope.histogram.bins;

          const values = _.map(counts, function(count, i) {
            const x0 = bins[i];
            const x1 = bins[i + 1];
            return {
              x0: x0,
              x1: x1,
              x: (x1 + x0) / 2,
              y: count
            };
          });

          const valuesByX = _.indexBy(values, (v) => xTickFormat(v.x));

          const data = [{
            values: values
          }];

          const minMax = calcMinMax(bins);

          chart
            .forceX([minMax.min, minMax.max])
            .interactiveLayer.tooltip.headerFormatter(function(d) {
              const format = $filter('number');
              const v = valuesByX[d];
              return format(v.x0) + ' - ' + format(v.x1);
            });

          d3.select(selector)
            .datum(data)
            .transition()
            .duration(ANIMATION_DURATION)
            .call(chart);

          registerResizeListener($scope, $window, chart.update);
        });
      };
    }
  };
}]).
directive('magpieBarChart', ['$timeout', '$window', function($timeout, $window) {
  return {
    restrict: 'E',
    scope: {
      id: '=',
      type: '=',
      chartData: '=',
      title: '=',
      min: '=?',
      max: '=?',
      xAxisFormatter: '=?',
      headerFormatter: '=?'
    },
    templateUrl: 'components/magpie-display/util/bar-chart-template.html',
    compile: function() {
      return function linkFunction($scope, element) {
        const chart = nv.models
          .historicalBarChart()
          .margin(historicalBarChartMargin)
          .useInteractiveGuideline(true);

        chart.xAxis
          .showMaxMin(false);

        chart.yAxis
          .showMaxMin(false)
          .tickFormat(d3.format(',.0f'));

        chart.color(getMagpieChartColors());

        $timeout(function() {
          const selector = getChartSelector(element);

          let minMax;
          if (_.isUndefined($scope.min) || _.isUndefined($scope.max)) {
            minMax = calcMinMax(_.map($scope.chartData, 'value'));
          } else {
            minMax = $scope;
          }
          const min = minMax.min;
          const max = minMax.max;

          const ticks = Math.min(max - min + 1, 6);

          const countsByValue = _.mapValues(_.indexBy($scope.chartData, 'value'), 'count');

          const values = [];
          for (let x = min; x <= max; x++) {
            const count = countsByValue[x];
            values.push({
              x: x,
              y: count || 0
            });
          }

          const data = [{
            values: values
          }];

          chart
            .forceX([min - 0.5, max + 0.5])
            .xAxis.ticks(ticks);

          if (!_.isUndefined($scope.xAxisFormatter)) {
            chart.xAxis.tickFormat($scope.xAxisFormatter);
          }

          if (!_.isUndefined($scope.headerFormatter)) {
            chart.interactiveLayer.tooltip.headerFormatter($scope.headerFormatter);
          }

          d3.select(selector)
            .datum(data)
            .transition()
            .duration(ANIMATION_DURATION)
            .call(chart);

          registerResizeListener($scope, $window, chart.update);
        });
      };
    }
  };
}]).
directive('magpieDiscreteBarChart', ['$timeout', '$window', function($timeout, $window) {
  return {
    restrict: 'E',
    scope: {
      id: '=',
      type: '=',
      chartData: '=',
      title: '=?'
    },
    templateUrl: 'components/magpie-display/util/bar-chart-template.html',
    compile: function() {
      return function linkFunction($scope, element) {
        const valueFormat = d3.format(',.0f');

        // make minimum left margin 40
        let maxValue = 10000;
        for (let i = 0; i < $scope.chartData.length; i++) {
          if (maxValue < $scope.chartData[i].value) {
            maxValue = $scope.chartData[i].value;
          }
        }
        // set leftMargin based on number of digits in the max value shown
        const leftMargin = maxValue.toString().length * 8;

        const chart = nv.models
          .discreteBarChart()
          .margin({
            top: 10,
            right: 10,
            bottom: 50,
            left: leftMargin
          })
          .x((d) => d.label)
          .y((d) => d.value)
          .showValues(true)
          .showXAxis(false)
          .valueFormat(valueFormat);

        chart.yAxis
          .showMaxMin(false)
          .tickFormat(valueFormat);

        chart.color(getMagpieChartColors());

        $timeout(function() {
          const selector = getChartSelector(element);

          const data = [{
            values: $scope.chartData
          }];

          const total = _($scope.chartData)
            .map('value')
            .sum();

          chart.tooltip.valueFormatter((d) => {
            const value = valueFormat(d);
            const percent = valueFormat((d * 100.0)/total);
            return '<span class="tooltip-value">' + value + ' </span>&nbsp;' +
            '<span class="tooltip-percent">(' + percent + '%)</span>';
          });

          d3.select(selector)
            .datum(data)
            .transition()
            .duration(ANIMATION_DURATION)
            .call(chart);

          registerResizeListener($scope, $window, chart.update);
        });
      };
    }
  };
}]).
directive('magpieError', function() {
  return {
    restrict: 'E',
    scope: {
      exception: '=',
      level: '@'
    },
    templateUrl: 'components/magpie-display/util/magpie-error.html',
    controller: function($scope) {
      $scope.isCollapsed = true;
    }
  };
}).
directive('magpieProportionChart', function() {
  return {
    restrict: 'E',
    scope: {
      passValue: '=',
      nullValue: '=',
      failValue: '='
    },
    templateUrl: 'components/magpie-display/util/proportion-chart.html'
  };
}).
controller('MagpieExpandableRowCtrl', function($scope, $element, $timeout) {
  const detailsEl = $element.find('.collapse').collapse();
  $scope.open = false;
  $scope.toggleDetails = function() {
    $scope.open = !$scope.open;
    $timeout(function() {
      if ($scope.open) {
        detailsEl.collapse('show');
      } else {
        detailsEl.collapse('hide');
      }
    });
  };
});



// WEBPACK FOOTER //
// ./src/components/magpie-display/util/magpie-util.js